package com.bikompanion.domain.usecase

import dev.zacsweers.metro.Inject
import com.bikompanion.domain.BikeStation
import com.bikompanion.domain.BikeType
import com.bikompanion.domain.StationWatchRepository
import com.bikompanion.domain.StationWatchUpdate
import com.bikompanion.domain.analytics.AnalyticsTracker
import com.bikompanion.domain.analytics.DomainAnalyticsEvents

@Inject
class EvaluateStationWatchesUseCase(
    private val repository: StationWatchRepository,
    private val analytics: AnalyticsTracker,
) {
    operator fun invoke(
        stationsByNetwork: Map<String, List<BikeStation>>,
        refreshedNetworkIds: Set<String>,
        nowEpochMillis: Long,
    ): StationWatchUpdate {
        val expired = repository.removeExpired(nowEpochMillis)
        expired.forEach { analytics.track(DomainAnalyticsEvents.stationWatchExpired(it)) }
        val active = repository.watches()
        val found = active.mapNotNull { watch ->
            if (watch.networkId !in refreshedNetworkIds) return@mapNotNull null
            val station = stationsByNetwork[watch.networkId]
                ?.firstOrNull { it.id == watch.stationId }
                ?.takeIf { it.hasAvailable(watch.bikeType) }
                ?: return@mapNotNull null
            watch.copy(
                stationName = station.name,
                station = station,
            )
        }
        if (found.isNotEmpty()) repository.clear()
        found.forEach { watch ->
            val startedAt = watch.expiresAtEpochMillis - StationWatchDuration.inWholeMilliseconds
            analytics.track(DomainAnalyticsEvents.stationWatchFound(watch, waitedMillis = nowEpochMillis - startedAt))
        }
        return StationWatchUpdate(
            activeWatches = if (found.isEmpty()) active else emptyList(),
            foundWatches = found,
            expiredWatches = expired,
        )
    }
}

private fun BikeStation.hasAvailable(type: BikeType): Boolean =
    when (type) {
        BikeType.Normal -> normalBikes > 0
        BikeType.Electric -> electricBikes > 0
    }
